import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Container,
} from "@mui/material";
import Background from "../../components/Background";
import Sidebar from "../../components/Sidebar";
import Mobilesidebar from "../../components/Mobilesidebar";
import { baseFetch } from "../../helpers/apiClient";
import { formatPrice } from "../../helpers/currency";
import { useDispatch } from "react-redux";
import { toggleSessionModal } from "../../redux/sessionSlice";
import moment from "moment";
import MenteeHeader from "../../components/MenteeHeader";
import DashboardFooter from "../../components/Footer";

function TransactionsList() {
  const dispatch = useDispatch();
  const [transactionListData, setTransactionListData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const gridSizes = { sm: 8, md: 9, lg: 9 };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const fetchTransactionList = () => {
    baseFetch({
      url: "/v1/web/transaction/all?page=1&limit=10",
      method: "GET",
    })
      .then((response) => {
        // Fall back to an empty list: the render path reads `.length`, so an
        // absent `results` would blank the page.
        setTransactionListData(response?.data?.results ?? []);
      })
      .catch((err) => {
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };

  useEffect(() => {
    fetchTransactionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Background>
        <MenteeHeader
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
        />
        <Box
          sx={{
            padding: {
              xs: "10px 0 20px",
              sm: "10px 0 20px",
              md: "10px 0 20px",
              lg: "10px 0 20px",
              xl: "20px 0 30px",
            },
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <Mobilesidebar />
                <Sidebar />
              </Grid>

              {/* Main Content Section */}
              <Grid item xs={12} {...gridSizes}>
                <Box
                  sx={{
                    height: "76vh", // Fixed height
                    overflow: "auto", // Enable scrolling if content exceeds the height
                    padding: "24px",
                    borderRadius: "12px",
                    backgroundColor: "white",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #E0E0E0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "left",
                      marginBottom: "2px",
                      fontWeight: "bold",
                      color: "#2A4876",
                      fontSize: {
                        xs: "16px",
                        sm: "18px",
                        md: "20px",
                        lg: "1.25rem",
                      },
                    }}
                  >
                    Past Purchases
                  </Typography>
                  <Box>
                    {/* Table */}
                    {transactionListData.length > 0 ? (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              #
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Time
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Transaction ID
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Category
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                textAlign: "right",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Amount
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: "#2A4876",
                                textAlign: "right",
                                borderBottom: "1px solid #AAB6C8",
                                fontSize: {
                                  xs: "14px",
                                  sm: "14px",
                                  md: "15px",
                                  lg: "15px",
                                  xl: "16px",
                                },
                              }}
                            >
                              Payment Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transactionListData.map((transaction, index) => (
                            <TableRow
                              key={transaction.id}
                              sx={{
                                "&:hover": { backgroundColor: "#F0F0F0" },
                                borderBottom: "none",
                              }}
                            >
                              <TableCell
                                sx={{
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {String(index + 1).padStart(2, "0")}
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {moment(transaction.createdAt).format(
                                  "DD MMM YYYY",
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {moment(transaction.createdAt).format(
                                  "hh:mm A",
                                )}
                              </TableCell>
                              <TableCell
                                sx={{
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {transaction.transactionId}
                              </TableCell>
                              <TableCell
                                sx={{
                                  textTransform: "capitalize",
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {transaction.type}
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: "right",
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {/* The backend returns dollars, not cents. Render as-is. */}
                                ${formatPrice(transaction.amount, true)}
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: "right",
                                  border: "none",
                                  color: "#152134",
                                  fontSize: {
                                    xs: "14px",
                                    sm: "14px",
                                    md: "14px",
                                    lg: "15px",
                                    xl: "16px",
                                  },
                                }}
                              >
                                {transaction?.paymentStatus}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box
                        style={{
                          justifyContent: "center",
                          height: "60vh",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        No purchase available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Background>
      <DashboardFooter />
    </>
  );
}

export default TransactionsList;
